import React from 'react';
import { CDataTable, CRow, CCol, CSwitch } from '@coreui/react';
import { ISorter, IPaginationParams, IFilter } from '@transferz/models';
import { ItemsPerPage } from './ItemsPerPage';
import { DataTableFilter } from './DataTableFilter';
import { DataTableHeader } from './DataTableHeader';
import { DataTablePagination } from './DataTablePagination';

interface IDataTableProps {
  data: any;
  fields: any;
  totalEntries?: number;
  filterByOptions?: { label: string; key: string; }[];
  onPaginationChange?: ({ page, size }: IPaginationParams) => void;
  onFilterChange?: ({ filterBy, filterValue }: IFilter) => void;
  onSorterChange?: (values: ISorter[]) => void;
  filterValue?: string;
  tableSorterValues?: ISorter[];
  filterBy?: string;
  itemsPerPage?: number;
  showItemsPerPage?: boolean;
  showFilter?: boolean;
  showToggle?: boolean;
  toggleLabel?: string;
  isToggleOn?: boolean;
  setIsToggleOn?: (value: boolean) => void;
  activePage?: number;
  loading?: boolean;
  showPagination?: boolean;
  autocompleteResults?: any[];
  reflectFiltersInUrl?: boolean;
  onOptionsSearch?: (query: string) => void;
  onActiveFilterChanged?: (filter: string) => void;
  [key: string]: any;
}

export const DataTable: React.FunctionComponent<IDataTableProps> = ({
  data,
  fields,
  onFilterChange,
  totalEntries = 0,
  onPaginationChange,
  onSorterChange,
  tableSorterValues = [],
  itemsPerPage = 10,
  filterByOptions,
  filterValue = '',
  filterBy = '',
  showItemsPerPage = true,
  showFilter = true,
  showToggle = false,
  isToggleOn,
  setIsToggleOn,
  toggleLabel = '',
  activePage = 1,
  loading,
  showPagination = true,
  noItemsViewText = 'No items',
  autocompleteResults = [],
  onOptionsSearch,
  onActiveFilterChanged,
  reflectFiltersInUrl = false,
  ...props
}) => (
  <>
    {(showItemsPerPage || showFilter) && (
      <CRow className="mb-2">
        {showItemsPerPage && (
          <CCol>
            <ItemsPerPage
              itemsPerPage={itemsPerPage}
              onChange={(size) => {
                if (onPaginationChange) {
                  onPaginationChange({ size, page: 1 });
                }
              }}
            />
          </CCol>
        )}
        {showFilter && filterByOptions && (
          <CCol sm="8">
            <DataTableFilter
              showToggle={showToggle}
              toggleLabel={toggleLabel}
              isToggleOn={isToggleOn}
              setIsToggleOn={setIsToggleOn}
              onActiveFilterChanged={onActiveFilterChanged}
              filterValue={filterValue}
              filterBy={filterBy}
              filterByOptions={filterByOptions}
              reflectFiltersInUrl={reflectFiltersInUrl}
              autocompleteResults={autocompleteResults}
              onOptionsSearch={onOptionsSearch}
              onFilterChange={(filterValue: any, activeFilter: string) => {
                if (onFilterChange) {
                  onFilterChange({ filterValue, filterBy: activeFilter });
                }
              }}
            />
          </CCol>
        )}
      </CRow>
    )}
    <CDataTable
      items={data}
      fields={fields}
      header={false}
      striped={true}
      hover={true}
      theadTopSlot={(
        <DataTableHeader
          fields={fields}
          sorterValues={tableSorterValues}
          onSorterChange={onSorterChange}
        />
      )}
      underTableSlot={showPagination && (
        <DataTablePagination
          page={activePage}
          totalEntries={totalEntries}
          itemsPerPage={itemsPerPage}
          onChange={(page) => {
            if (onPaginationChange) {
              onPaginationChange({ size: itemsPerPage, page });
            }
          }}
        />
      )}
      loading={loading}
      noItemsView={{noResults: noItemsViewText, noItems: noItemsViewText }}
      noItemsViewSlot={loading && (!data || !data.length) ? <div className="emptyRows" /> : undefined}
      {...props}
    />
  </>
);
